import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Canale, LetturaCanale } from '@emacomfort/domain';
import { useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { Badge, Card, Pagina, cx, stili } from '@/ui';
import { useSondaggio, useTecnico } from '../comune';
import s from '../features.module.css';

export function etichettaAssegnazione(t: (k: string, o?: Record<string, unknown>) => string, c: Canale, zone: Array<{ id: string; nome: string }>): string {
  if (c.assegnazione.tipo === 'zona') return `${t('tecnico.io.zona')}: ${zone.find((z) => z.id === (c.assegnazione as { zonaId: string }).zonaId)?.nome ?? c.assegnazione.zonaId}`;
  if (c.assegnazione.tipo === 'funzione') return t(`tecnico.funzione.${c.assegnazione.funzione}`) + (c.assegnazione.generatoreId ? ` · ${c.assegnazione.generatoreId}` : '');
  return t('tecnico.canali.nonAssegnato');
}

export function CanaliPage() {
  const { t } = useTranslation();
  const f = useFormato();
  const { esegui } = useTecnico();
  const impianto = useStato((st) => st.impianto);
  const attuatori = useStato((st) => st.attuatori);
  const carica = useCallback(() => esegui((tec, sess) => tec.getCanali(sess)), [esegui]);
  const { dati } = useSondaggio<LetturaCanale[]>(carica, 2000);
  if (!impianto) return null;
  const zone = impianto.zone;
  const ingressi = (dati ?? []).filter((c) => c.canale.direzione === 'INPUT');
  const uscite = (dati ?? []).filter((c) => c.canale.direzione === 'OUTPUT');

  return (
    <Pagina titolo={t('tecnico.canali.titolo')} sottotitolo={t('tecnico.canali.sottotitolo')}>
      <Card titolo={t('tecnico.canali.moduli')}>
        <div className={stili.riga}>
          {impianto.moduli.map((m) => (
            <Badge key={m.id} tono={m.stato === 'ok' ? 'ok' : 'err'}>
              {m.nome} · {t(`tecnico.canali.moduloStato.${m.stato}`)}
            </Badge>
          ))}
        </div>
      </Card>
      <Card titolo={t('tecnico.canali.ingressi')}>
        <div className={stili.scorrevole}>
          <table className={stili.tabella}>
            <thead>
              <tr>
                <th>{t('tecnico.canali.canale')}</th>
                <th>{t('tecnico.canali.assegnazione')}</th>
                <th>{t('tecnico.canali.grezzo')}</th>
                <th>{t('tecnico.canali.convertito')}</th>
                <th>{t('tecnico.canali.qualita')}</th>
                <th>{t('tecnico.canali.modulo')}</th>
              </tr>
            </thead>
            <tbody>
              {ingressi.map((l) => (
                <tr key={l.canale.id}>
                  <td className={s.canaleId}>{l.canale.id}</td>
                  <td>
                    {etichettaAssegnazione(t, l.canale, zone)}
                    {l.canale.confidenza && l.canale.confidenza !== 'alta' && (
                      <>
                        {' '}
                        <Badge tono="warn">{t(`tecnico.canali.confidenza.${l.canale.confidenza}`)}</Badge>
                      </>
                    )}
                    {l.canale.nota && <div className={s.letturaNota}>{t(`note.${l.canale.nota}`)}</div>}
                  </td>
                  <td className={cx(stili.mono)}>{l.grezzo === null ? '— —' : l.grezzo}</td>
                  <td className={cx(stili.mono)}>{l.convertito === null ? '— —' : l.canale.grandezza === 'TEMPERATURA' ? f.temp(l.convertito) : `${f.numero(l.convertito, 0)} ${l.canale.unita}`}</td>
                  <td>
                    <Badge tono={l.q === 'ok' ? 'ok' : 'err'}>{t(`tecnico.canali.qualitaValore.${l.q}`)}</Badge>
                  </td>
                  <td className={stili.muto}>{impianto.moduli.find((m) => m.id === l.canale.moduloId)?.nome ?? l.canale.moduloId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card titolo={t('tecnico.canali.uscite')}>
        <div className={stili.scorrevole}>
          <table className={stili.tabella}>
            <thead>
              <tr>
                <th>{t('tecnico.canali.canale')}</th>
                <th>{t('tecnico.canali.assegnazione')}</th>
                <th>{t('tecnico.canali.stato')}</th>
                <th>{t('tecnico.canali.modulazione')}</th>
                <th>{t('tecnico.canali.modulo')}</th>
              </tr>
            </thead>
            <tbody>
              {uscite.map((l) => {
                const a = attuatori[l.canale.id];
                return (
                  <tr key={l.canale.id}>
                    <td className={s.canaleId}>{l.canale.id}</td>
                    <td>
                      {etichettaAssegnazione(t, l.canale, zone)}
                      {l.canale.nota && <div className={s.letturaNota}>{t(`note.${l.canale.nota}`)}</div>}
                    </td>
                    <td>
                      <Badge tono={a?.stato === 'attivo' ? 'ok' : 'neutro'}>{a?.stato === 'attivo' ? t('comune.attivo') : t('comune.spento')}</Badge>
                      {a?.forzatoFinoA && (
                        <>
                          {' '}
                          <Badge tono="warn">{t('tecnico.canali.forzato', { ora: f.ora(a.forzatoFinoA) })}</Badge>
                        </>
                      )}
                    </td>
                    <td className={stili.mono}>{a?.modulazione === undefined ? '—' : f.pct(a.modulazione)}</td>
                    <td className={stili.muto}>{impianto.moduli.find((m) => m.id === l.canale.moduloId)?.nome ?? l.canale.moduloId}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </Pagina>
  );
}
